/**
 * The calm water, as numbers — ported from home-ceo-mobile
 * `src/components/home/wee-water.tsx` and `wee-stars.tsx` (named `eee-*`
 * at main @ 8d85fd1), the backdrop the app's Explore and Enact pages sit
 * on (`WeeBackdrop story={false}`).
 *
 * Most values here were tuned on device; the comments say which, and
 * what a change would cost. Re-measure before moving one.
 */

import { ALIVE_AT, CUE, OMEGA, SELECTION_AT, enter, glide, seeded } from '@components/wee/wee-timeline';
import type { HowLight, WaterStory } from './water-story';

/** The app authors the water on a stage 1080 units wide; y positions are fractions of the stage height. */
export const STAGE = 1080;

/**
 * CSS pixels per stage unit, at most. The app scales the stage to the
 * screen's width, about 0.36 px per unit on a phone. Scaled the same
 * way, a 1440 px window would draw every band and caustic cell almost
 * four times the size it is on the phone, so on wide screens the stage
 * grows instead: the water keeps roughly twice its phone size and more
 * of it shows.
 */
const UNIT_PX_MAX = 0.75;

/** How deep calm water sits, as a share of the story's full depth. Constant in calm mode. */
export const CALM_DEPTH = 0.35;

/** The light (bands and caustics) softens while the water is deep. */
export const LIGHT = 1 - 0.5 * CALM_DEPTH;

/** The bands are the broad slow swell beneath the caustics, at a little over half the presence they had alone. */
export const BAND_PRESENCE = 0.55;

/**
 * Six soft strokes of light whose shape flows: each is two sines of
 * different wavelengths travelling opposite ways. Speeds `k1`/`k2` are
 * whole turns per cycle. Amplitude and speed were cut to a third once
 * and measured under one luminance level of change across two seconds
 * on device — flowing in code, still to the eye.
 */
export const BANDS = [
  { y: 0.06, width: 76, a1: 38, a2: 20, l1: 720, l2: 440, k1: 2, k2: -3, strength: 1 },
  { y: 0.17, width: 70, a1: 34, a2: 18, l1: 640, l2: 400, k1: -2, k2: 3, strength: 0.9 },
  { y: 0.29, width: 66, a1: 32, a2: 17, l1: 680, l2: 420, k1: 2, k2: -2, strength: 0.8 },
  { y: 0.42, width: 62, a1: 30, a2: 16, l1: 620, l2: 380, k1: -1, k2: 2, strength: 0.7 },
  { y: 0.56, width: 58, a1: 28, a2: 15, l1: 700, l2: 430, k1: 1, k2: -2, strength: 0.6 },
  { y: 0.72, width: 54, a1: 26, a2: 14, l1: 640, l2: 400, k1: -1, k2: 2, strength: 0.5 },
] as const;
/** Gaussian sigma of each band's wide body, stage units. */
export const BAND_BODY_BLUR = 14;
export const BAND_RIDGE_WIDTH = 12;
export const BAND_RIDGE_BLUR = 5;
/** The slow rise and fall of a whole band, stage units, once per cycle. */
export const BAND_BOB = 16;

/** ~7 cells per stage width: about 55 pt on a phone, chosen to match this site's old ocean photo. */
export const CAUSTIC_SCALE = 7.0;
/** Shader seconds per real second. */
export const CAUSTIC_SPEED = 0.32;
/**
 * Both high on purpose: the water sits near 84% luminance, and at half
 * strength the web measured ~13 of 255 levels on device — painting but
 * invisible. Now ridges lift ~30 levels and peaks ~40, while the cells
 * between stay untouched, so the page's average colour does not move.
 */
export const CAUSTIC_INTENSITY = 1.6;
export const CAUSTIC_OPACITY = 0.9;
/**
 * The caustic fold runs its four iterations at -2.5, -0.75, -1/6 and
 * 1/8 of shader time, so it repeats exactly every 48π. Folding the
 * uniform onto that period keeps float32 precise however long the tab
 * stays open, and changes no frame.
 */
export const CAUSTIC_PERIOD = 48 * Math.PI;

/** Reduced motion holds one still frame: the app's pick moment plus 0.9 s, with caustic time at zero. */
export const STILL_T = SELECTION_AT + 0.9;

/** The far tier of glints the app draws over the water on a phone. */
const FAR_STAR_COUNT = 12;
/** A phone's stage area (1080 × 1080·844/390), for keeping the same density of stars on bigger stages. */
const PHONE_STAGE_AREA = STAGE * ((STAGE * 844) / 390);
const MAX_STARS = 36;

export type StageMetrics = {
  /** CSS pixels per stage unit. */
  unit: number;
  /** Stage size in stage units. */
  W: number;
  H: number;
  /** How many glints to draw at this size. */
  stars: number;
};

export function stageMetrics(cssWidth: number, cssHeight: number): StageMetrics {
  const unit = Math.min(cssWidth / STAGE, UNIT_PX_MAX);
  const W = cssWidth / unit;
  const H = cssHeight / unit;
  const stars = Math.min(
    MAX_STARS,
    Math.max(FAR_STAR_COUNT, Math.round((FAR_STAR_COUNT * W * H) / PHONE_STAGE_AREA)),
  );
  return { unit, W, H, stars };
}

export type StarState = {
  /** Even stars are four-point glints, odd ones soft bokeh discs. */
  glint: boolean;
  x: number;
  y: number;
  /** Glint box size, or bokeh radius — stage units. */
  span: number;
  /** Radians, applied inside the glint's unit box (it pivots on the box's corner, as in the app). */
  rotation: number;
  alpha: number;
};

/**
 * Where every star is at story time `t`. Each index keeps its own home
 * position, so a resize that changes the count adds or removes stars at
 * the end rather than reshuffling the field.
 *
 * The brightness follows the story's beat even though the water is
 * calm — a quirk of the app kept for parity. It is continuous across
 * the wrap, so it reads as the light slowly breathing.
 */
export function starStates(t: number, W: number, H: number, count: number): StarState[] {
  const beat =
    0.5 +
    0.5 * glide(t, 0, 1, CUE.wish, CUE.wish + 1.2) -
    0.4 * glide(t, 0, 1, CUE.explore, CUE.explore + 1) +
    0.4 * glide(t, 0, 1, ALIVE_AT - 0.5, ALIVE_AT + 0.5) -
    0.5 * glide(t, 0, 1, CUE.loop - 1.4, CUE.loop);

  const out: StarState[] = [];
  for (let i = 0; i < count; i++) {
    const glint = i % 2 === 0;
    const x0 = W * (0.05 + seeded(i, 1) * 0.9);
    const y0 = H * (0.08 + seeded(i, 2) * 0.84);
    const rise = H * (0.05 + seeded(i, 4) * 0.07);
    const span = glint ? 24 + seeded(i, 3) * 18 : 9 + seeded(i, 3) * 9;
    // Two to four twinkles per cycle, so no two flicker alike.
    const twinkleTurns = 2 + Math.round(seeded(i, 7) * 2);
    const twinkle = 0.45 + 0.55 * (0.5 + 0.5 * Math.sin(t * twinkleTurns * OMEGA + i));
    out.push({
      glint,
      x: x0 + 22 * Math.sin(t * OMEGA + seeded(i, 5) * 6.28),
      y: y0 - rise * Math.sin(t * OMEGA + seeded(i, 8) * 6.28),
      span,
      rotation: t * OMEGA + seeded(i, 6) * 3,
      alpha: beat * twinkle * (glint ? 0.95 : 0.8),
    });
  }
  return out;
}

// --- the story ------------------------------------------------------

/**
 * What the water adds when it tells the story — the app's
 * `WeeBackdrop story={true}` layers, in stage units. The water deepens
 * while the wish forms, takes on soft colour as the options arrive, and
 * a sunrise climbs to the house as it comes alive, then drains through
 * white to the plain surface before the cycle wraps.
 */
export type StoryFrame = {
  /** How deep the water is: calm water holds at CALM_DEPTH. */
  depth: number;
  /** The bands' and caustics' strength, which softens as the water deepens. */
  light: number;
  wash: { opacity: number; x0: number; y0: number; x1: number; y1: number };
  sunrise: { opacity: number; cx: number; cy: number; r: number };
  sunlit: number;
  /** How much of the Result section's colour swirl covers the water. */
  swirl: number;
  /** How far How we build's night has brought the lights down (a veil over the water, under the glints). */
  night: number;
};

export const CALM_FRAME: StoryFrame = {
  depth: CALM_DEPTH,
  light: LIGHT,
  wash: { opacity: 0, x0: 0, y0: 0, x1: 1, y1: 0 },
  sunrise: { opacity: 0, cx: 0, cy: 0, r: 1 },
  sunlit: 0,
  swirl: 0,
  night: 0,
};

/**
 * The Result section's swirl: the Enact sunrise's colours as soft lights
 * drifting on slow orbits over a warm white, so the page stays in the
 * glow the story ended on. Laid out as the mock's Result page is — gold
 * high on the left, peach high on the right, rose through the middle,
 * lavender low, and the water's own seafoam in the far corner.
 *
 * `x`/`y` are the light's home as a share of the screen; `r` its radius
 * as a share of the screen's height; `turns` how many times it circles
 * its home, across and down, per SWIRL_PERIOD (a turn every 30 to 60
 * seconds); `alpha` how strongly it tints.
 */
export const SWIRL_LIGHTS = [
  { color: 'sunriseGold', x: 0.06, y: 0.02, r: 0.58, turns: [9, 7], alpha: 0.36 },
  { color: 'sunriseGold', x: 0.9, y: 0.04, r: 0.45, turns: [7, 5], alpha: 0.22 },
  { color: 'sunrisePeach', x: 0.86, y: 0.5, r: 0.4, turns: [5, 7], alpha: 0.1 },
  { color: 'sunriseRose', x: 0.22, y: 0.62, r: 0.5, turns: [6, 8], alpha: 0.12 },
  { color: 'sunriseLilac', x: 0.3, y: 1.05, r: 0.55, turns: [8, 6], alpha: 0.2 },
  { color: 'seafoam', x: 1.02, y: 1.02, r: 0.42, turns: [5, 4], alpha: 0.55 },
] as const;

/**
 * The swirl repeats exactly every five minutes: every rate in it is a
 * whole number of turns per period, so its clock wraps there without a
 * jump — the same rule the story's OMEGA keeps.
 */
export const SWIRL_PERIOD = 300;

/** How far each light strays from home, as a share of the screen. */
export const SWIRL_ORBIT = 0.14;

/** How much of the water still shows through a full swirl: a little of its light. */
export const SWIRL_COVER = 0.97;

/**
 * The cursor's wake — what the water does under a resting page's
 * pointer. Distances are CSS pixels, so the wake is the same size on a
 * phone-sized window as on a wide one; the bend it applies is in stage
 * units, against a swell whose own waves stand 26 to 38 units tall.
 */
export const WAKE = {
  /** How far from the cursor the water answers. */
  radiusPx: 260,
  /** Distance between the rings travelling outward. */
  ringPx: 132,
  /** How fast those rings travel, px per second. */
  speedPx: 110,
  /**
   * How far the water's own coordinates are pulled toward and away from
   * the cursor, at full strength. Nothing is drawn for the wake: this
   * pull is the whole of it, so the swell and the caustic that were
   * already there are what ripples outward.
   *
   * It has to be big. A caustic cell is ~154 units across, so a pull of
   * a third of that slid the sparkle too little to see against its own
   * drift (~3 of 255 levels per 60ms); two thirds bends it plainly.
   */
  pull: 105,
  /** How much more the refracted light gathers under the cursor. */
  sparkle: 0.5,
  /** Seconds for the wake to rise when the pointer arrives, and to settle when it goes. */
  rise: 0.5,
  fall: 1.1,
} as const;

/**
 * The wake's clock wraps on a whole number of rings — a hundred of
 * them — so a tab left open for hours keeps float32 precision and the
 * wrap costs no visible jump, the rule SWIRL_PERIOD and OMEGA keep.
 */
export const WAKE_PERIOD = (WAKE.ringPx / WAKE.speedPx) * 100;

/** Where the pointer is and how strongly the water is answering it, in CSS px. */
export type Wake = {
  x: number;
  y: number;
  /** 0 while the page plays or the pointer is away; 1 once it has arrived. */
  strength: number;
  /** Seconds, wrapped at WAKE_PERIOD. */
  time: number;
};

/** Where the sunrise settles when nothing says where the house is: the app's default, a third of the way down. */
const SUNRISE_Y = 0.36;

/**
 * The story's layers at story time `story.t`, faded in by `story.weight`
 * — so as the section scrolls away the water eases back to calm rather
 * than cutting to it. `story.focus` is in CSS px; the stage is too, once
 * divided by `m.unit`, because the canvas covers the viewport from its
 * corner.
 */
export function storyFrame(story: WaterStory, m: StageMetrics): StoryFrame {
  return withHowLight(weeFrame(story, m), story.how, m);
}

/**
 * How we build's light over whatever else the water shows: its night as
 * the veil; its dawn as the WEE story's own sunrise, rising where the
 * story says; its day as the same sunlit wash the house comes alive in.
 */
function withHowLight(frame: StoryFrame, how: HowLight, m: StageMetrics): StoryFrame {
  if (!(how.night > 0 || how.dawn > 0 || how.day > 0)) return frame;
  const dawn = Math.min(1, how.dawn);
  return {
    ...frame,
    night: Math.max(frame.night, Math.min(1, how.night)),
    sunrise:
      dawn > frame.sunrise.opacity
        ? {
            opacity: dawn,
            cx: how.focus ? how.focus.x / m.unit : m.W / 2,
            cy: how.focus ? how.focus.y / m.unit : m.H * SUNRISE_Y,
            r: STAGE * 1.05,
          }
        : frame.sunrise,
    sunlit: Math.max(frame.sunlit, Math.min(1, how.day)),
  };
}

function weeFrame(story: WaterStory, m: StageMetrics): StoryFrame {
  const w = story.weight;
  const swirl = Math.max(0, Math.min(1, story.swirl));
  if (!(w > 0)) return swirl > 0 ? { ...CALM_FRAME, swirl } : CALM_FRAME;
  const t = story.t;

  const storyDepth =
    glide(t, 0, 1, CUE.wish + 0.1, CUE.wish + 1.6) *
    glide(t, 1, 0.72, CUE.explore, CUE.explore + 1.2) *
    glide(t, 1, 0, CUE.enact + 0.3, CUE.enact + 2.4);
  const depth = CALM_DEPTH + (storyDepth - CALM_DEPTH) * w;

  // The app sweeps its wash a quarter of the stage either way across a
  // stage it spans 1.4 times; the web's stage is as wide as the window.
  const sway = 0.25 * m.W * Math.sin(t * OMEGA);
  const focusX = story.focus ? story.focus.x / m.unit : m.W / 2;
  const focusY = story.focus ? story.focus.y / m.unit : m.H * SUNRISE_Y;

  return {
    depth,
    light: 1 - 0.5 * depth,
    wash: {
      opacity:
        w *
        glide(t, 0, 1, CUE.explore - 1.8, CUE.explore + 0.2) *
        glide(t, 1, 0, CUE.explore + 1.6, CUE.explore + 3.2),
      x0: -0.2 * m.W + sway,
      y0: 0,
      x1: 1.2 * m.W + sway,
      y1: 0.9 * m.H,
    },
    sunrise: {
      opacity: w * enter(t, CUE.enact + 0.2, 2.0) * glide(t, 1, 0, ALIVE_AT + 1.2, CUE.loop - 1.2),
      cx: focusX,
      cy: glide(t, m.H * 1.08, focusY, CUE.enact + 0.2, CUE.enact + 2.6),
      r: STAGE * 1.05 * (1 + 0.035 * Math.sin((t - CUE.enact) * 1.3)),
    },
    sunlit: w * enter(t, ALIVE_AT + 0.6, 1.8) * glide(t, 1, 0, CUE.loop - 1.5, CUE.loop - 0.3),
    swirl,
    night: 0,
  };
}

/** `#RRGGBB` or `rgba(r, g, b, a)` as 0–1 floats, for handing tokens to a shader or a canvas. */
export function rgba(color: string): [number, number, number, number] {
  const hex = /^#([0-9a-f]{6})$/i.exec(color);
  if (hex) {
    const n = parseInt(hex[1], 16);
    return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255, 1];
  }
  const fn = /^rgba?\(([^)]+)\)$/i.exec(color);
  if (fn) {
    const [r, g, b, a = '1'] = fn[1].split(',').map((s) => s.trim());
    return [Number(r) / 255, Number(g) / 255, Number(b) / 255, Number(a)];
  }
  throw new Error(`water: unsupported colour ${color}`);
}
